import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../../auth';
import { useCurrency, useCurrencyBusy } from '../keno/CurrencyContext';
import { useGameSession } from '../keno/GameSession';
import { useRevealedStats } from '../keno/useRevealedStats';
import { useWalletPresentation } from '../keno/useWalletPresentation';
import { useGamePreferences } from '../keno/useGamePreferences';
import { withWalletTurn } from '../keno/walletTurns';
import { readDemoWallet, saveDemoWallet as writeDemoWallet, toMinor } from '../keno/credits';
import { useKenoSound } from '../keno/useKenoSound';
import { gameHeaders, KENO_API, loadFairness, rotateFairness } from '../keno/fairnessApi';
import { originalApi, originalConfig, originalWallet } from './gameApi';
import { OriginalVerifyForm } from './OriginalVerifyForm';
import { DEFAULT_STRATEGY, parseStrategy, strategyNextBet, strategyReachedLimit } from './autoStrategy';
import { useDiceRollSound } from './useDiceRollSound';
import { useOriginalEffects } from './useOriginalEffects';
import { useBalanceSync } from '../keno/useBalanceSync';
import { useOriginalsAccess } from './useOriginalsAccess';
import { usePlinkoDrops } from './usePlinkoDrops';
import { parseAutoBetCount, autoBetDelay } from './autoBetting';
import { recordSessionRecentBet, clearSessionRecentBets } from './sessionRecentBets';
import { rateLimitedRequest } from './rateLimitedRequest';

const DEMO_INITIAL = { balance_minor: 100000, vault_minor: 0, revision: 0, rounds: [] };
export const useOriginalGame = (gameType) => {
  const { user, loading } = useAuth();
  const access = useOriginalsAccess(user);
  const { addRound } = useGameSession();
  const currency = useCurrency();
  const demo = !user, owner = user ? user.id + (currency.accountDemo ? ':account-demo' : '') : 'demo';
  const { stageRound, revealRound } = useRevealedStats(owner, gameType);
  const { reserveStake, confirmWallet, revealPayout, cancelStake, presentWallet } = useWalletPresentation(owner, gameType);
  const revealStats = useCallback((revealed) => { revealPayout(revealed); revealRound(revealed); }, [revealPayout, revealRound]);
  const [config, setConfig] = useState(null), [wallet, rawWallet] = useState({ balance_minor: 0, vault_minor: 0, revision: 0, rounds: [] });
  const [round, rawRound] = useState(null), [fairness, rawFairness] = useState(null), [walletLoading, setWalletLoading] = useState(true);
  const [bet, setBet] = useState('1.00'), [mode, setMode] = useState('manual'), [count, setCount] = useState('0');
  const [mines, setMines] = useState(3), [selected, setSelected] = useState([]), [targetMultiplier, setTargetMultiplier] = useState('2.00');
  const [direction, setDirection] = useState('over'), [target, setTarget] = useState('50.49');
  const [busy, setBusy] = useState(false), [auto, setAuto] = useState(false), [remaining, setRemaining] = useState(0), [error, setError] = useState('');
  const { instant, turbo, muted, volume, setInstant, setTurbo, setMuted, setVolume, settingsControls, beginRound, getPreferences } = useGamePreferences(owner, auto, busy);
  const [ghostBusy, setGhostBusy] = useState(false);
  const [retryNotice, setRetryNotice] = useState('');
  const [fairnessBusy, setFairnessBusy] = useState(false), rotating = useRef(false);
  const [queuedTiles, setQueuedTiles] = useState([]);
  const mineQueue = useRef([]), drainingMines = useRef(false);
  const [advanced, setAdvanced] = useState(() => ({ ...DEFAULT_STRATEGY }));
  const sound = useKenoSound(muted, volume);
  const effects = useOriginalEffects(muted, volume);
  const recordPlinkoLanding = useCallback((landed) => { revealPayout(landed); addRound(landed, owner); }, [addRound, owner, revealPayout]);
  const plinko = usePlinkoDrops(gameType !== 'plinko' || muted, volume, recordPlinkoLanding);
  const resetPlinko = plinko.reset;
  useDiceRollSound(gameType === 'dice' && busy && !instant, muted, volume);
  const version = useRef(0), walletRef = useRef(wallet), roundRef = useRef(null), seedRef = useRef(null), sending = useRef(false), stopping = useRef(false), autoRunning = useRef(false), pendingDrop = useRef(false);
  const turnPending = useRef(false);
  const setWallet = useCallback((value) => { walletRef.current = value; rawWallet(value); }, []);
  useBalanceSync({ owner, demo, walletRef, apply: setWallet, isBusy: () => sending.current || autoRunning.current || rotating.current, poll: true });
  const setRound = useCallback((value) => { roundRef.current = value; rawRound(value); }, []);
  const setFairness = useCallback((value) => { seedRef.current = value; rawFairness(value); }, []);
  useEffect(() => {
    const current = ++version.current;
    setRetryNotice('');
    resetPlinko();
    stopping.current = true; sending.current = false; autoRunning.current = false; rotating.current = false; pendingDrop.current = false; setFairnessBusy(false); mineQueue.current = []; drainingMines.current = false; setQueuedTiles([]); setSelected([]); setBusy(false); setAuto(false); setRound(null); setFairness(null); setError(''); setWalletLoading(true);
    if (loading) return;
    (async () => {
      const seed = await loadFairness(demo);
      const [cfg, balance, state] = await Promise.all([originalConfig(gameType, demo), demo ? Promise.resolve(readDemoWallet()) : originalWallet(), originalApi(gameType, 'state', demo)]);
      if (current !== version.current) return;
      setConfig(cfg); setWallet(balance); setFairness(seed); setRound(state.round && !state.round.finished ? state.round : null);
      if (state.round && !state.round.finished) { setMines(state.round.params.mines); setBet((state.round.bet_minor / 100).toFixed(2)); }
    })().catch((failure) => { if (current === version.current) setError(formatApiErrorDetail(failure.response?.data?.detail || 'Unable to load game.')); })
      .finally(() => { if (current === version.current) setWalletLoading(false); });
    return () => { version.current = current + 1; stopping.current = true; };
  }, [demo, gameType, loading, owner, setFairness, setRound, setWallet, resetPlinko]);

  const send = async (action = 'bet', tile, betOverride = null) => {
    if (turnPending.current) {
      if (gameType === 'plinko' && !autoRunning.current) pendingDrop.current = true;
      return null;
    }
    turnPending.current = true;
    const current = version.current;
    try { return await withWalletTurn(owner, () => current === version.current && (betOverride === null || !stopping.current) ? sendNow(action, tile, betOverride) : null); }
    finally {
      turnPending.current = false;
      if (current === version.current && pendingDrop.current) {
        pendingDrop.current = false;
        if (gameType === 'plinko' && !autoRunning.current && toMinor(bet) <= walletRef.current.balance_minor) send();
      }
    }
  };
  const sendNow = async (action = 'bet', tile, betOverride = null) => {
    if (action === 'bet' && (access.blocked || access.loading || access.error)) return null;
    if (gameType === 'plinko' && plinko.active.current.length >= 12) { setError('Wait for a ball to land before dropping another.'); return null; }
    if (sending.current || rotating.current || !seedRef.current) { if (gameType === 'plinko' && action === 'bet' && betOverride === null && sending.current && !autoRunning.current) pendingDrop.current = true; return null; }
    const quiet = gameType === 'plinko';
    const { instant, turbo } = beginRound();
    sending.current = true; if (!quiet) setBusy(true); setError('');
    if (gameType === 'plinko') plinko.sound.prepare();
    else effects.prepare();
    if (gameType === 'plinko') { /* Sounds follow the confirmed ball path. */ }
    else if (gameType === 'mines' && action === 'bet') effects.play('place');
    else sound(action === 'bet' ? 'bet' : 'pick');
    const current = version.current;
    const requestId = crypto.randomUUID();
    if (action === 'bet') reserveStake(requestId, betOverride ?? toMinor(bet));
    let presentation = null;
    try {
      const balance = demo ? readDemoWallet() : walletRef.current;
      const payload = { request_id: requestId, revision: balance.revision, ...(demo ? { demo_balance_minor: balance.balance_minor } : {}) };
      if (action === 'bet') Object.assign(payload, { ...seedRef.current, bet_minor: betOverride ?? toMinor(bet), mines, picks: gameType === 'mines' && mode === 'auto' ? selected : [], target_e8: Math.round(Number(targetMultiplier) * 1e8), direction, target: Math.round(Number(target) * 100) });
      if (gameType === 'plinko' && action === 'bet') Object.assign(payload, { rows: plinko.rows, risk: plinko.risk });
      else if (action !== 'bet') Object.assign(payload, { action, tile, round_id: roundRef.current.request_id, round_revision: roundRef.current.round_revision });
      if (gameType === 'plinko') plinko.reserve(requestId);
      const headers = gameHeaders(demo);
      const onWait = (seconds) => { if (current === version.current) setRetryNotice(`Request limit reached. Retrying in ${seconds}s…`); };
      const data = await rateLimitedRequest(() => originalApi(gameType, action === 'bet' ? 'bet' : 'action', demo, payload, headers), {
        cancelled: () => current !== version.current || (betOverride !== null && stopping.current), onWait,
      });
      if (data.round.finished && gameType !== 'plinko') presentation = stageRound(data.round);
      if (current !== version.current) {
        if (gameType === 'plinko' && data.round.finished) addRound(data.round, owner);
        return null;
      }
      confirmWallet(requestId, data.round);
      const next = { ...balance, balance_minor: data.balance_minor, revision: demo ? balance.revision + 1 : data.revision };
      if (data.round.finished) {
        next.rounds = [...(balance.rounds || []).filter((r) => r.request_id !== data.round.request_id), data.round].slice(-50);
        next.recent = { ...balance.recent, [gameType]: [...(balance.recent?.[gameType] || []).filter((r) => r.request_id !== data.round.request_id), data.round].slice(-100) };
        if (gameType === 'limbo') recordSessionRecentBet(owner, data.round);
      }
      if (demo) writeDemoWallet(next);
      if (gameType === 'plinko') plinko.add(data.round, instant, turbo);
      setWallet(next); setRound(data.round);
      if (gameType === 'limbo' && !instant && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) effects.play('rise');
      if (gameType === 'mines' && data.round.finished && !data.round.win) effects.play('bomb');
      if (action === 'bet') {
        const nextSeed = await rateLimitedRequest(() => loadFairness(demo), { cancelled: () => current !== version.current, onWait });
        if (current !== version.current) return null;
        setFairness(nextSeed);
      }
      if (presentation) await presentation;
      if (current === version.current && gameType !== 'plinko' && !(gameType === 'mines' && data.round.finished && !data.round.win)) sound(data.round.finished ? data.round.payout_minor > data.round.bet_minor ? 'win' : data.round.payout_minor < data.round.bet_minor ? 'loss' : 'hit' : 'hit');
      return data.round;
    } catch (failure) {
      cancelStake(requestId);
      if (failure.code === 'AUTO_RETRY_CANCELLED') return null;
      if (current === version.current) {
        setError(formatApiErrorDetail(failure.response?.status === 429 ? 'Request limit reached. Auto stopped safely; review the last completed bet before continuing.' : failure.response?.data?.detail || 'Unable to complete bet. Review the current round.'));
        try { const [seed, state, balance] = await Promise.all([loadFairness(demo), originalApi(gameType, 'state', demo), demo ? Promise.resolve(readDemoWallet()) : originalWallet()]); if (current === version.current) { setFairness(seed); setRound(state.round); setWallet(balance); } } catch { /* Keep the last confirmed state visible. */ }
      }
      return null;
    } finally {
      if (presentation) await presentation;
      if (current === version.current) {
        setRetryNotice('');
        if (gameType === 'plinko') plinko.release(requestId);
        sending.current = false; if (!quiet) setBusy(false);
      }
    }
  };
  const start = async () => {
    if (autoRunning.current) { stopping.current = true; return; }
    if (mode !== 'auto') { await send(); return; }
    let limit;
    try { limit = parseAutoBetCount(count); } catch (failure) { setError(failure.message); return; }
    let strategy;
    try { strategy = parseStrategy(advanced); } catch (failure) { setError(failure.message); return; }
    const baseBet = toMinor(bet);
    let nextBet = baseBet, net = 0;
    const current = version.current;
    stopping.current = false; autoRunning.current = true; setAuto(true);
    for (let i = 0; i < limit && !stopping.current && current === version.current; i += 1) {
      setRemaining(limit === Infinity ? null : limit - i);
      if (!nextBet || nextBet > config.max_bet_minor || nextBet > walletRef.current.balance_minor) { setError('Auto stopped: the next wager exceeds your balance or bet limit.'); break; }
      setBet((nextBet / 100).toFixed(2));
      const result = await send('bet', undefined, nextBet);
      if (!result) break;
      net += result.payout_minor - result.bet_minor;
      if (strategyReachedLimit(strategy, net)) break;
      nextBet = strategyNextBet(strategy, nextBet, baseBet, result);
      const speed = getPreferences();
      const delay = autoBetDelay(gameType, speed.instant, speed.turbo);
      if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
    }
    if (current === version.current) { autoRunning.current = false; setAuto(false); setRemaining(0); }
  };
  const updateGhost = async (ghost) => {
    setGhostBusy(true);
    try { const { data } = await axios.put(`${KENO_API}/keno/preferences`, { ghost }, { headers: gameHeaders(false) }); setWallet({ ...walletRef.current, ghost: data.ghost }); }
    catch (failure) { setError(formatApiErrorDetail(failure.response?.data?.detail || 'Unable to update privacy.')); }
    finally { setGhostBusy(false); }
  };
  const refreshFairness = async () => {
    const current = version.current;
    const seed = await loadFairness(demo);
    if (current === version.current) setFairness(seed);
  };
  const rotateSeed = async (clientSeed) => {
    if (sending.current || rotating.current || autoRunning.current || mineQueue.current.length || plinko.active.current.length || (roundRef.current && !roundRef.current.finished)) throw new Error('Finish your current game before changing seeds.');
    if (!seedRef.current) throw new Error('Wait for your seed pair to load.');
    const current = version.current;
    rotating.current = true; setFairnessBusy(true);
    try {
      const value = await rotateFairness(demo, seedRef.current, clientSeed);
      if (current === version.current) setFairness(value.current);
      return value;
    } finally { if (current === version.current) { rotating.current = false; setFairnessBusy(false); } }
  };
  const active = !!round && !round.finished;
  const queuePick = (tile) => {
    const current = roundRef.current;
    if (!current || current.finished || current.revealed.includes(tile) || mineQueue.current.includes(tile)) return;
    mineQueue.current.push(tile); setQueuedTiles([...mineQueue.current]);
  };
  useEffect(() => {
    if (gameType !== 'mines') return;
    if (!active) { if (mineQueue.current.length) { mineQueue.current = []; setQueuedTiles([]); } return; }
    if (busy || drainingMines.current || !mineQueue.current.length) return;
    const current = version.current, tile = mineQueue.current[0];
    drainingMines.current = true;
    send('reveal', tile).then((result) => {
      if (current !== version.current) return;
      if (!result || result.finished) mineQueue.current = [];
      else mineQueue.current = mineQueue.current.filter((queued) => queued !== tile);
    }).finally(() => {
      if (current === version.current) { drainingMines.current = false; setQueuedTiles([...mineQueue.current]); }
    });
  }, [active, busy, queuedTiles, gameType]); // eslint-disable-line react-hooks/exhaustive-deps
  const resetDemo = () => { if (!demo || busy || auto || active || plinko.active.current.length) return; const next = { ...DEMO_INITIAL, vault_minor: 0, revision: walletRef.current.revision + 1 }; clearSessionRecentBets(owner); writeDemoWallet(next); setWallet(next); setRound(null); resetPlinko(); };
  const syncWallet = (value) => setWallet({ ...value, rounds: [...new Map([...(walletRef.current.rounds || []), ...(value.rounds || [])].map((entry) => [`${entry.game || 'keno'}-${entry.request_id}`, entry])).values()].sort((a, b) => a.created_at.localeCompare(b.created_at)).slice(-50) });
  useCurrencyBusy(owner, busy || fairnessBusy || auto || active || plinko.drops.length > 0);
  return { gameType, access, Verifier: OriginalVerifyForm, config, wallet: { ...wallet, currency: currency.accountDemo ? 'demo' : 'v' }, setWallet, round: gameType === 'plinko' ? plinko.last : round, hiddenHistoryIds: plinko.hiddenHistoryIds, fairness, setFairness, demo, owner, walletLoading: loading || walletLoading || !currency.ready,
    bet, setBet, mode, setMode, count, setCount, advanced, setAdvanced, mines, setMines, selected, setSelected, targetMultiplier, setTargetMultiplier, direction, setDirection, target, setTarget,
    busy: busy || fairnessBusy, auto, remaining, active, error, retryNotice, displayWallet: { ...presentWallet(wallet), currency: currency.accountDemo ? 'demo' : 'v' }, revealStats, start, send, queuedTiles, queuePick, instant, setInstant, turbo, setTurbo, muted, setMuted, volume, setVolume, settingsControls, updateGhost, ghostBusy, resetDemo, syncWallet, rotateSeed, refreshFairness, dismissResult: () => { if (roundRef.current?.finished) setRound(null); }, picking: false, fairnessLocked: active || plinko.drops.length > 0, plinko };
};